// src/layouts/hamburger-button.tsx
"use client";

import React, { useState, useCallback } from "react";
import { ActionIcon, Drawer } from "rizzui";
import cn from "@core/utils/class-names";

export type DrawerPlacements = "left" | "right" | "top" | "bottom";

interface Props {
  /** The panel content—e.g. your Sidebar */
  view: React.ReactNode;
  /** Which side it slides in from */
  placement?: DrawerPlacements;
  /** Icon extra classes */
  className?: string;
  /** Tailwind width class for the panel itself */
  drawerWidthClass?: string;
}

export default function HamburgerButton({
  view,
  placement = "left",
  className,
  drawerWidthClass = "w-[230px]",
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  // closes when clicking any <a> inside the panel
  const handleInnerClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("a")) {
      setIsOpen(false);
    }
  }, []);

  return (
    <>
      <ActionIcon
        aria-label="Toggle Sidebar"
        variant="text"
        className={cn("me-3 xl:hidden", className)}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="h-6 w-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5"
          />
        </svg>
      </ActionIcon>

      <Drawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement={placement}
        // full-screen overlay
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        // panel width + slide animation
        containerClassName={cn(
          drawerWidthClass,
          "transform transition-transform duration-300 ease-in-out",
          // when closed, push fully offscreen
          isOpen
            ? "translate-x-0"
            : placement === "left"
              ? "-translate-x-full"
              : "translate-x-full"
        )}
        closeOnOverlayClick
      >
        {/* catch link-clicks */}
        <div onClick={handleInnerClick}>{view}</div>
      </Drawer>
    </>
  );
}
